/**
 * Head-to-head card on the Profile page (specs/profile-page AC-17..19):
 * one row per friend the viewer has actually played, showing the win–loss record.
 * No aggregate/overall number. Shows an empty state when there are no rivalries.
 */

import React from 'react';
import { View, Text, Pressable } from 'react-native';
import { FriendAvatar } from '../../../../components/friends/shared/FriendAvatar';
import type { HeadToHeadRow } from '../../helpers/profileStats.helper';
import { PROFILE_COLORS, PROFILE_TEST_IDS } from '../ProfileScreen/ProfileScreen.constants';
import { headToHeadCardStyles as styles } from './HeadToHeadCard.styles';

export interface HeadToHeadCardProps {
  rows: HeadToHeadRow[];
  /** True when the friends list failed to load — show retry, never a silent empty state. */
  hasError?: boolean;
  onRetry?: () => void;
  testID?: string;
}

export const HeadToHeadCard: React.FC<HeadToHeadCardProps> = ({ rows, hasError, onRetry, testID }) => (
  <View style={styles.card} testID={testID ?? PROFILE_TEST_IDS.h2hCard}>
    <Text style={styles.title}>Head-to-head</Text>

    {hasError ? (
      <View style={styles.errorBlock} testID={PROFILE_TEST_IDS.h2hError}>
        <Text style={styles.empty}>We couldn&apos;t load your head-to-head records.</Text>
        <Pressable
          onPress={onRetry}
          accessibilityRole="button"
          accessibilityLabel="Retry head-to-head"
          testID={PROFILE_TEST_IDS.h2hRetry}
          style={({ pressed }) => [styles.retry, pressed && styles.retryPressed]}
        >
          <Text style={styles.retryText}>Try again</Text>
        </Pressable>
      </View>
    ) : rows.length === 0 ? (
      <Text style={styles.empty} testID={PROFILE_TEST_IDS.h2hEmpty}>
        Play a friend to start a rivalry — your records show up here.
      </Text>
    ) : (
      rows.map((r) => (
        <View key={r.id} style={styles.row}>
          <FriendAvatar
            avatarId={r.avatarId}
            avatarConfig={r.avatarConfig}
            fallbackUrl={r.avatarUrl ?? undefined}
            nameForInitials={r.name}
            size={32}
            avatarRounded
            small
          />
          <Text style={styles.name} numberOfLines={1}>vs {r.name}</Text>
          <Text style={[styles.record, { color: r.ahead ? PROFILE_COLORS.mint : PROFILE_COLORS.textMuted }]}>
            {r.record}
          </Text>
        </View>
      ))
    )}
  </View>
);
